import { useEffect, useState } from 'react';
import { scheduledScansApi } from '../../services/scheduledScansApi';
import { matchScheduledStatusToWorkflow } from './currentScanTruth';
import { resolveScheduledRunScanOutcome, scheduledRunCreatedScanHref } from './scheduledRunScanOutcome';
import { buildScheduledScanStatusRows } from './workspaceScheduledScanStatus';

export type CaseScheduledScanFacts = {
  latestRunLabel: string | null;
  latestRunStatus: string | null;
  scanOutcomeLabel: string | null;
  scanOutcomeReason: string | null;
  scanCreatedHref: string | null;
  nextScheduledLabel: string | null;
};

const EMPTY_FACTS: CaseScheduledScanFacts = {
  latestRunLabel: null,
  latestRunStatus: null,
  scanOutcomeLabel: null,
  scanOutcomeReason: null,
  scanCreatedHref: null,
  nextScheduledLabel: null,
};

export function useCaseScheduledScanFacts(
  siteId?: string | null,
  workflowId?: string | null,
): CaseScheduledScanFacts {
  const [facts, setFacts] = useState<CaseScheduledScanFacts>(EMPTY_FACTS);
  const trimmedSite = String(siteId || '').trim();
  const trimmedWorkflow = String(workflowId || '').trim();

  useEffect(() => {
    if (!trimmedSite || !trimmedWorkflow) {
      setFacts(EMPTY_FACTS);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const summary = await scheduledScansApi.getSiteStatusSummary(trimmedSite, 100);
        if (cancelled) return;
        const rows = buildScheduledScanStatusRows(summary.items || []);
        const match = matchScheduledStatusToWorkflow(rows, trimmedWorkflow);
        if (!match) {
          setFacts(EMPTY_FACTS);
          return;
        }
        const outcome = resolveScheduledRunScanOutcome({
          id: match.runId,
          status: match.rawStatus,
          terminal_reason_code: match.terminalReasonCode,
          terminal_reason_message: match.terminalReasonMessage,
          links: {
            result_summary_id: match.resultSummaryId,
            report_id: match.reportId,
          },
        });
        setFacts({
          latestRunLabel: match.occurrenceLabel !== '—' ? match.occurrenceLabel : null,
          latestRunStatus: match.statusLabel,
          scanOutcomeLabel: outcome.scanOutcomeLabel,
          scanOutcomeReason: outcome.scanOutcomeReason,
          scanCreatedHref: outcome.scanCreated
            ? scheduledRunCreatedScanHref({
              siteId: trimmedSite,
              reportId: outcome.createdScanId,
            })
            : null,
          nextScheduledLabel: match.nextOccurrenceLabel,
        });
      } catch {
        if (!cancelled) setFacts(EMPTY_FACTS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trimmedSite, trimmedWorkflow]);

  return facts;
}
